import { roundToDecimals } from "./formatPercent";

/**
 * Chart.js datasets for Advanced Student Reports (shared with Reports & Analytics snapshot).
 * granularity: 0 = department, 1 = class, 2 = individual
 */
export const REPORT_GRANULARITY = { DEPARTMENT: 0, CLASS: 1, INDIVIDUAL: 2 };

export const REPORT_PERIOD_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

/** Exam type for analytics APIs (omit or `all` = all types in backend). */
export const REPORT_EXAM_TYPE_OPTIONS = [
  { value: "all", label: "All exam types" },
  { value: "quiz", label: "Quiz" },
  { value: "unitTest", label: "Unit test" },
  { value: "midTerm", label: "Mid term" },
  { value: "final", label: "Final" },
];

/**
 * Attendance Breakdown donut.
 *
 * Shows Present % vs Absent % ONLY (no "Late"). We deliberately derive the split
 * from the authoritative average-attendance percentage rather than summing raw
 * slot-level present/absent/late marks: unmarked-absent students never generate
 * an "absent" slot row, so raw sums make Present look ~100% and contradict the
 * headline "Avg Attendance" figure. Using the same percentage as the headline
 * keeps the donut consistent (e.g. 52.95% present / 47.05% absent).
 */
export function buildAttendanceDoughnutChart({ granularity, data, selectedStudent }) {
  const toDonut = (presentPctRaw) => {
    const presentPct = roundToDecimals(Math.min(100, Math.max(0, Number(presentPctRaw) || 0)));
    const absentPct = roundToDecimals(Math.max(0, 100 - presentPct));
    return {
      labels: ["Present %", "Absent %"],
      datasets: [
        {
          data: [presentPct, absentPct],
          backgroundColor: ["#4caf50", "#f44336"],
          borderWidth: 0,
        },
      ],
    };
  };

  if (granularity === REPORT_GRANULARITY.INDIVIDUAL && data?.attendance) {
    return toDonut(data.attendance.percentage || 0);
  }

  const rows =
    granularity === REPORT_GRANULARITY.DEPARTMENT
      ? data?.departments || []
      : data?.classes || [];

  // Authoritative average attendance (same value as the "Avg Attendance" headline).
  const summaryAvg = data?.summary?.avgAttendanceExact ?? data?.summary?.avgAttendance;
  let avgPct = summaryAvg != null ? Number(summaryAvg) : 0;

  // Fallback: average the per-row attendance percentages when no summary value exists.
  if (!avgPct && rows.length > 0) {
    const validRows = rows.filter((r) => r.attendance?.percentage != null);
    if (validRows.length > 0) {
      avgPct = validRows.reduce((sum, r) => sum + Number(r.attendance.percentage || 0), 0) / validRows.length;
    }
  }

  return toDonut(avgPct);
}

export function buildComparisonBarChart({ granularity, data }) {
  const rows =
    granularity === REPORT_GRANULARITY.DEPARTMENT
      ? data?.departments || []
      : data?.classes || [];
  return {
    labels: rows.map((r) => r.name || ""),
    datasets: [
      {
        label: "Attendance %",
        data: rows.map((r) => roundToDecimals(r.attendance?.percentage || 0)),
        backgroundColor: "#1976d2",
      },
      {
        label: "Assignment completion %",
        data: rows.map((r) => roundToDecimals(r.assignments?.completionRate || 0)),
        backgroundColor: "#9c27b0",
      },
      {
        label: "Exam marks %",
        data: rows.map((r) => roundToDecimals(r.marks?.percent ?? 0)),
        backgroundColor: "#ed6c02",
      },
    ],
  };
}
